import { useState, useEffect } from "react"
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
} from "@mui/material"
import { apiFetch } from "@/hooks/apiFetch"
import { useConfigurationBranch } from "@/hooks/useConfigurationBranch"
import { toast } from "sonner"

const POSConfig = () => {
  const { branchId, ready, branchSelector, settingsUrl } =
    useConfigurationBranch()
  const [settingsLoaded, setSettingsLoaded] = useState(false)
  const [invoiceMessage, setInvoiceMessage] = useState("")
  const [invoicePrefix, setInvoicePrefix] = useState("INV")
  const [notificationEmail, setNotificationEmail] = useState("")
  const [reportTime, setReportTime] = useState("")
  const [reportEmail, setReportEmail] = useState("")
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!ready) return
    let active = true
    setSettingsLoaded(false)
    setFieldErrors({})
    fetchSettings(() => active)
    return () => {
      active = false
    }
  }, [branchId, ready])

  const fetchSettings = async (isCurrent: () => boolean) => {
    try {
      const data = await apiFetch<any>(settingsUrl("settings/pos-config"), {
        method: "GET",
      })
      if (data && isCurrent()) {
        setSettingsLoaded(true)
        setInvoiceMessage(data.invoice_message || "")
        setInvoicePrefix(data.invoice_prefix || "INV")
        setNotificationEmail(data.notification_email || "")
        setReportTime(data.daily_report_time || "")
        setReportEmail(data.daily_report_email || "")
      }
    } catch (error) {
      console.error("Failed to fetch pos settings", error)
    }
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSaving(true)
    setFieldErrors({})
    try {
      await apiFetch<any>(settingsUrl("settings/pos-config"), {
        method: "POST",
        body: JSON.stringify({
          invoice_message: invoiceMessage,
          invoice_prefix: invoicePrefix.trim() || null,
          notification_email: notificationEmail,
          daily_report_time: reportTime || null,
          daily_report_email: reportEmail.trim() || null,
        }),
      })
      toast.success("Pos configuration saved successfully")
    } catch (error: any) {
      setFieldErrors(
        Object.fromEntries(
          Object.entries(error?.error || {}).map(([field, messages]) => [
            field,
            (messages as string[])[0],
          ])
        )
      )
      console.error("Failed to save POS settings", error)
      toast.error("Failed to save POS configuration")
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 4 }}>
      <Typography variant="h5" sx={{ fontWeight: "bold" }}>
        POS Configuration
      </Typography>
      {branchSelector}

      <Card sx={{ maxWidth: 800 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            POS settings
          </Typography>
          <form onSubmit={onSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <TextField
                label="Invoice Message"
                type="text"
                fullWidth
                value={invoiceMessage}
                onChange={(e) => setInvoiceMessage(e.target.value)}
                placeholder="Enter invoice message"
                helperText='Used to display the message at the end of the invoice. Default "THANK YOU FOR SHOPPING WITH US!"'
              />

              <TextField
                label="Invoice Number Prefix"
                type="text"
                fullWidth
                value={invoicePrefix}
                onChange={(e) => {
                  setInvoicePrefix(e.target.value.toUpperCase())
                  setFieldErrors((previous) => ({
                    ...previous,
                    invoice_prefix: "",
                  }))
                }}
                placeholder="INV"
                inputProps={{ maxLength: 20 }}
                error={!!fieldErrors.invoice_prefix}
                helperText={
                  fieldErrors.invoice_prefix ||
                  "Invoice numbers look like INV0001 and count per branch. Changing the prefix starts the count again from 0001."
                }
              />

              <TextField
                label="Notification Email"
                type="email"
                fullWidth
                value={notificationEmail}
                onChange={(e) => setNotificationEmail(e.target.value)}
                placeholder="alerts@example.com"
                error={!!fieldErrors.notification_email}
                helperText={
                  fieldErrors.notification_email ||
                  "Used for website order notifications."
                }
              />

              <Box
                sx={{ borderTop: "1px solid", borderColor: "divider", pt: 3 }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Daily dashboard report
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Receive an Excel summary of all payment modes, sales returns,
                  and daily expenses for all brands. Totals are captured at the
                  selected time each day.
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 2fr" },
                    gap: 3,
                  }}
                >
                  <TextField
                    label="Daily Report Time"
                    type="time"
                    fullWidth
                    value={reportTime}
                    onChange={(event) => {
                      setReportTime(event.target.value)
                      setFieldErrors((previous) => ({
                        ...previous,
                        daily_report_time: "",
                      }))
                    }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 60 }}
                    error={!!fieldErrors.daily_report_time}
                    helperText={
                      fieldErrors.daily_report_time ||
                      "Nepal time (Asia/Kathmandu)"
                    }
                  />
                  <TextField
                    label="Daily Report Email"
                    type="email"
                    fullWidth
                    value={reportEmail}
                    onChange={(event) => {
                      setReportEmail(event.target.value)
                      setFieldErrors((previous) => ({
                        ...previous,
                        daily_report_email: "",
                      }))
                    }}
                    placeholder="admin@example.com"
                    error={!!fieldErrors.daily_report_email}
                    helperText={
                      fieldErrors.daily_report_email ||
                      "Recipient for the daily Excel attachment"
                    }
                  />
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 2, display: "block" }}
                >
                  Set both fields to enable daily reports. Clear both to turn
                  them off.
                </Typography>
              </Box>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!settingsLoaded || isSaving}
                sx={{ mt: 1, alignSelf: "flex-start", textTransform: "none" }}
              >
                {isSaving ? "Saving..." : "Save POS config"}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  )
}

export default POSConfig
